// Enhanced Insurance Workflows with BUILD/HOST/GOVERN Support
import { Agent, Workflow } from '../types';

// Native ElevateNow Agents (BUILD)
export const fullAgentSuite: Agent[] = [
  // Core Native Insurance Intelligence Agents
  {
    id: 'insurverify',
    name: 'InsurVerify',
    type: 'validator',
    status: 'idle',
    description: 'Validates submission data completeness and quality across carrier systems',
    capabilities: ['Cross-System Data Validation', 'ACORD Form Recognition', 'Quality Scoring', 'Smart Recommendations'],
    performance: {
      avgProcessingTime: 23,
      successRate: 98.7,
      tasksCompleted: 47293,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '2.1.3',
      deploymentType: 'managed'
    },
  },
  {
    id: 'exposureinsights',
    name: 'ExposureInsights',
    type: 'analyzer',
    status: 'idle',
    description: 'CAT risk analysis and exposure intelligence across multiple platforms',
    capabilities: ['CAT Exposure Analysis', 'Cross-Platform Aggregation', 'Geographic Intelligence', 'Portfolio Analytics'],
    performance: {
      avgProcessingTime: 45,
      successRate: 94.8,
      tasksCompleted: 31867,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '3.0.1',
      deploymentType: 'managed'
    },
  },
  {
    id: 'lossinsights',
    name: 'LossInsights',
    type: 'analyzer',
    status: 'idle',
    description: 'Consolidates loss history from disparate carrier systems with predictive analytics',
    capabilities: ['Multi-Carrier Loss Consolidation', 'Trend Analysis', 'Severity Prediction', 'Claims Intelligence'],
    performance: {
      avgProcessingTime: 67,
      successRate: 93.5,
      tasksCompleted: 28394,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '2.3.0',
      deploymentType: 'managed'
    },
  },
  {
    id: 'eligibilitychecker',
    name: 'EligibilityChecker',
    type: 'decisioner',
    status: 'idle',
    description: 'Real-time appetite matching with embedded regulatory compliance validation',
    capabilities: ['Appetite Validation', 'Regulatory Rule Enforcement', 'Market Intelligence', 'Decision Consistency'],
    performance: {
      avgProcessingTime: 18,
      successRate: 99.1,
      tasksCompleted: 52847,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '1.9.2',
      deploymentType: 'managed'
    },
  },
  {
    id: 'insurancecontrolsadvisor',
    name: 'InsuranceControlsAdvisor',
    type: 'validator',
    status: 'idle',
    description: 'Enterprise governance and audit control monitoring across business units',
    capabilities: ['Control Monitoring', 'Governance Orchestration', 'Exception Management', 'Audit Trail Intelligence'],
    performance: {
      avgProcessingTime: 12,
      successRate: 98.7,
      tasksCompleted: 41923,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '2.1.1',
      deploymentType: 'managed'
    },
  },
  {
    id: 'compliancechecker',
    name: 'ComplianceChecker',
    type: 'validator',
    status: 'idle',
    description: 'Embedded regulatory intelligence ensuring compliant operations across workflows',
    capabilities: ['Multi-Jurisdiction Compliance', 'Real-time Regulatory Updates', 'Automated Filing Prep', 'Risk-Based Controls'],
    performance: {
      avgProcessingTime: 15,
      successRate: 99.5,
      tasksCompleted: 48567,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '3.1.0',
      deploymentType: 'managed'
    },
  },
  {
    id: 'marketintel',
    name: 'MarketIntel',
    type: 'enricher',
    status: 'idle',
    description: 'External market intelligence integration across carrier platforms and systems',
    capabilities: ['Real-time Market Data', 'Competitive Intelligence', 'Industry Benchmarking', 'External API Orchestration'],
    performance: {
      avgProcessingTime: 34,
      successRate: 91.3,
      tasksCompleted: 19847,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '1.7.3',
      deploymentType: 'managed'
    },
  },
  {
    id: 'riskscorer',
    name: 'RiskScorer',
    type: 'analyzer',
    status: 'idle',
    description: 'External ML-based risk intelligence with market-wide scoring models',
    capabilities: ['Ensemble ML Models', 'Market Risk Benchmarking', 'External Data Integration', 'Predictive Analytics'],
    performance: {
      avgProcessingTime: 35,
      successRate: 94.2,
      tasksCompleted: 28456,
    },
    provider: 'hosted',
    provenance: {
      source: 'AWS Agent Code',
      vendor: 'Risk Analytics Corp',
      version: '2.4.1',
      deploymentType: 'hosted',
      integration: 'conductor'
    },
  },
  {
    id: 'documentextractor',
    name: 'DocumentExtractor',
    type: 'extractor',
    status: 'idle',
    description: 'Universal document intelligence across any carrier system or document format',
    capabilities: ['Universal OCR Processing', 'Cross-System Integration', 'Format Agnostic Extraction', 'Intelligent Classification'],
    performance: {
      avgProcessingTime: 21,
      successRate: 96.2,
      tasksCompleted: 67294,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '3.2.0',
      deploymentType: 'managed'
    },
  },

  // Hosted External Agents (HOST) - AWS Agent Code Examples
  {
    id: 'fraud-detector',
    name: 'FraudDetector',
    type: 'analyzer',
    status: 'idle',
    description: 'Advanced fraud detection with behavioral pattern analysis and network intelligence',
    capabilities: ['Behavioral Analysis', 'Network Pattern Detection', 'Risk Scoring', 'Investigation Triggers'],
    performance: {
      avgProcessingTime: 38,
      successRate: 92.4,
      tasksCompleted: 18923,
    },
    provider: 'hosted',
    provenance: {
      source: 'AWS Agent Code',
      vendor: 'InsurTech Analytics',
      version: '1.8.2',
      deploymentType: 'hosted',
      integration: 'conductor'
    },
  },
  {
    id: 'settlement-analyzer',
    name: 'SettlementAnalyzer',
    type: 'decisioner',
    status: 'idle',
    description: 'Market-based settlement recommendation engine with comparative analytics',
    capabilities: ['Settlement Benchmarking', 'Market Comparisons', 'Negotiation Intelligence', 'Reserve Optimization'],
    performance: {
      avgProcessingTime: 52,
      successRate: 89.7,
      tasksCompleted: 12847,
    },
    provider: 'hosted',
    provenance: {
      source: 'AWS Agent Code',
      vendor: 'Claims Intelligence Corp',
      version: '2.1.0',
      deploymentType: 'hosted',
      integration: 'conductor'
    },
  },
  {
    id: 'regulatory-monitor',
    name: 'RegulatoryMonitor',
    type: 'validator',
    status: 'idle',
    description: 'Native regulatory intelligence with deep insurance domain compliance knowledge',
    capabilities: ['Regulatory Intelligence', 'Compliance Automation', 'Rule Engine', 'Filing Preparation'],
    performance: {
      avgProcessingTime: 22,
      successRate: 98.4,
      tasksCompleted: 12847,
    },
    provider: 'native',
    provenance: {
      source: 'ElevateNow',
      certified: true,
      version: '1.3.4',
      deploymentType: 'managed'
    },
  },
];

// Enhanced Workflows with BUILD/HOST/GOVERN Orchestration
export const enhancedWorkflows: Workflow[] = [
  {
    id: 'submission-intake',
    name: 'Submission Intake & Analysis',
    description: 'Complete submission processing from broker portal through underwriting decision',
    status: 'active',
    orchestrationType: 'native',
    agentMix: 'all_native',
    conductorIntegration: 'full',
    steps: [
      {
        id: 'step1',
        agentId: 'documentextractor',
        name: 'Extract Submission Documents',
        config: { 
          documentTypes: ['ACORD 125', 'ACORD 140', 'SOV', 'Loss Runs'],
          qualityThreshold: 85,
          autoClassification: true
        },
        dependencies: [],
        outputs: ['extracted_data', 'document_classification', 'confidence_scores'],
      },
      {
        id: 'step2',
        agentId: 'insurverify',
        name: 'Validate Data Quality',
        config: { 
          validationRules: 'comprehensive',
          qualityThreshold: 90,
          recommendationEngine: true
        },
        dependencies: ['step1'],
        outputs: ['validation_report', 'quality_score', 'missing_data_flags'],
      },
      {
        id: 'step3',
        agentId: 'exposureinsights',
        name: 'Analyze CAT Exposure',
        config: { 
          models: ['RMS', 'AIR', 'KCC'],
          includeGeocoding: true,
          aggregationLevel: 'account'
        },
        dependencies: ['step2'],
        outputs: ['cat_analysis', 'pml_calculations', 'geographic_distribution'],
      },
      {
        id: 'step4',
        agentId: 'lossinsights',
        name: 'Process Loss History',
        config: { 
          lookbackPeriod: 5,
          includePriorCarriers: true,
          trendAnalysis: true
        },
        dependencies: ['step2'],
        outputs: ['loss_summary', 'frequency_severity', 'trend_indicators'],
      },
      {
        id: 'step5',
        agentId: 'eligibilitychecker',
        name: 'Check Appetite & Guidelines',
        config: { 
          guidelines: 'current_version',
          classCodeValidation: true,
          territoryCheck: true
        },
        dependencies: ['step3', 'step4'],
        outputs: ['eligibility_decision', 'appetite_score', 'restriction_flags'],
      },
      {
        id: 'step6',
        agentId: 'riskscorer',
        name: 'Generate Risk Assessment',
        config: { 
          models: ['property_risk', 'liability_risk'],
          explainableAI: true,
          benchmarking: true
        },
        dependencies: ['step3', 'step4', 'step5'],
        outputs: ['risk_score', 'score_drivers', 'peer_comparison'],
      },
    ],
    metrics: {
      avgCompletionTime: 4.2,
      successRate: 97.3,
      totalExecutions: 23847,
    },
  },
  {
    id: 'claims-processing',
    name: 'Claims Processing & Investigation',
    description: 'Automated claims intake through settlement recommendation with fraud detection',
    status: 'active',
    orchestrationType: 'hosted',
    agentMix: 'external_integration',
    conductorIntegration: 'cross_platform',
    steps: [
      {
        id: 'claim1',
        agentId: 'documentextractor',
        name: 'Process FNOL Documents',
        config: { 
          documentTypes: ['Police Reports', 'Photos', 'Estimates', 'Medical Records'],
          urgencyClassification: true,
          damageAssessment: true
        },
        dependencies: [],
        outputs: ['fnol_data', 'damage_assessment', 'urgency_score'],
      },
      {
        id: 'claim2',
        agentId: 'compliancechecker',
        name: 'Validate Coverage & Policy',
        config: { 
          policyValidation: true,
          coverageAnalysis: true,
          deductibleCalculation: true
        },
        dependencies: ['claim1'],
        outputs: ['coverage_determination', 'policy_analysis', 'payment_calculation'],
      },
      {
        id: 'claim3',
        agentId: 'fraud-detector',
        name: 'Fraud Detection Analysis',
        config: { 
          fraudModels: ['behavioral', 'pattern', 'network'],
          riskScoring: true,
          investigationTriggers: true
        },
        dependencies: ['claim1', 'claim2'],
        outputs: ['fraud_score', 'investigation_flags', 'similar_claims'],
      },
      {
        id: 'claim4',
        agentId: 'settlement-analyzer',
        name: 'Settlement Benchmarking',
        config: { 
          marketData: 'current',
          similarClaims: true,
          settlementRange: true
        },
        dependencies: ['claim2', 'claim3'],
        outputs: ['settlement_range', 'market_comparison', 'negotiation_guidance'],
      },
    ],
    metrics: {
      avgCompletionTime: 2.8,
      successRate: 96.4,
      totalExecutions: 31247,
    },
  },
  {
    id: 'regulatory-filing',
    name: 'Regulatory Filing & Compliance',
    description: 'Automated regulatory filing preparation and compliance monitoring across jurisdictions',
    status: 'active',
    orchestrationType: 'hybrid',
    agentMix: 'native_and_hosted',
    conductorIntegration: 'unified_governance',
    steps: [
      {
        id: 'reg1',
        agentId: 'regulatory-monitor',
        name: 'Monitor Regulatory Changes',
        config: { 
          jurisdictions: ['all_active'],
          sources: ['NAIC', 'State DOI', 'Federal'],
          alertThresholds: 'medium',
          impactAnalysis: true
        },
        dependencies: [],
        outputs: ['regulatory_updates', 'impact_assessment', 'filing_requirements'],
      },
      {
        id: 'reg2',
        agentId: 'documentextractor',
        name: 'Prepare Filing Documents',
        config: { 
          filingTypes: ['rate', 'form', 'market_conduct'],
          templateGeneration: true,
          dataValidation: true
        },
        dependencies: ['reg1'],
        outputs: ['filing_documents', 'supporting_data', 'validation_reports'],
      },
      {
        id: 'reg3',
        agentId: 'insurancecontrolsadvisor',
        name: 'Control & Audit Validation',
        config: { 
          controlFramework: 'SOX_Insurance',
          auditTrail: true,
          signoffProcess: true
        },
        dependencies: ['reg1', 'reg2'],
        outputs: ['control_validation', 'audit_evidence', 'approval_workflow'],
      },
      {
        id: 'reg4',
        agentId: 'compliancechecker',
        name: 'Update Business Rules',
        config: { 
          ruleUpdates: 'automatic',
          impactAnalysis: true,
          rollbackPlan: true
        },
        dependencies: ['reg1', 'reg3'],
        outputs: ['updated_rules', 'change_log', 'rollback_procedures'],
      },
    ],
    metrics: {
      avgCompletionTime: 6.1,
      successRate: 99.1,
      totalExecutions: 8429,
    },
  },
];

// Enhanced Orchestration Metrics with BUILD/HOST/GOVERN Intelligence
export const orchestrationMetrics = {
  'submission-intake': {
    decisionConsistency: 94.2,
    crossLobAlignment: 97.1,
    agentConsensusRate: 96.8,
    conductorTaskReliability: 99.3,
    nativeAgentPerformance: 98.1
  },
  'claims-processing': {
    decisionConsistency: 91.7,
    multiVendorCoordination: 93.4,
    hostedAgentReliability: 94.8,
    crossPlatformContextSharing: 92.1,
    integratedWorkflowSuccess: 95.2
  },
  'regulatory-filing': {
    hybridWorkflowReliability: 98.1,
    crossPlatformContextSharing: 97.9,
    complianceAuditScore: 99.7,
    nativeHostedCoordination: 96.4,
    governanceConsistency: 98.8
  }
};

// Platform Status for Enterprise Integration Reality
export const platformStatus = {
  crossPlatformContext: 'active',
  multiVendorCoordination: 'stable',
  conductorOrchestration: 'optimal',
  hostedAgentHealth: {
    'fraud-detector': 'operational',
    'settlement-analyzer': 'operational',
    'regulatory-monitor': 'operational'
  },
  versionCompatibility: 'verified',
  governanceFramework: 'compliant'
};

// Enhanced workflow execution status with realistic timing
export const workflowExecutions = [
  {
    workflowId: 'submission-intake',
    executions: [
      { id: 'exec-1', status: 'completed', startTime: new Date(Date.now() - 300000), duration: 4.1 },
      { id: 'exec-2', status: 'processing', startTime: new Date(Date.now() - 180000), currentStep: 'step4' },
      { id: 'exec-3', status: 'processing', startTime: new Date(Date.now() - 60000), currentStep: 'step2' },
      { id: 'exec-4', status: 'queued', startTime: new Date(Date.now() - 30000) },
      { id: 'exec-5', status: 'completed', startTime: new Date(Date.now() - 900000), duration: 3.9 },
    ]
  },
  {
    workflowId: 'claims-processing',
    executions: [
      { id: 'exec-6', status: 'completed', startTime: new Date(Date.now() - 240000), duration: 2.7 },
      { id: 'exec-7', status: 'processing', startTime: new Date(Date.now() - 120000), currentStep: 'claim3' },
      { id: 'exec-8', status: 'completed', startTime: new Date(Date.now() - 600000), duration: 2.9 },
      { id: 'exec-9', status: 'processing', startTime: new Date(Date.now() - 45000), currentStep: 'claim2' },
    ]
  },
  {
    workflowId: 'regulatory-filing',
    executions: [
      { id: 'exec-10', status: 'processing', startTime: new Date(Date.now() - 90000), currentStep: 'reg3' },
      { id: 'exec-11', status: 'completed', startTime: new Date(Date.now() - 450000), duration: 5.8 },
      { id: 'exec-12', status: 'completed', startTime: new Date(Date.now() - 720000), duration: 6.3 },
    ]
  }
];

// Agent workload with enhanced provider awareness
export const agentWorkload = {
  // Native agents
  'insurverify': { current: 34, capacity: 80, trend: 'up', provider: 'native' },
  'exposureinsights': { current: 28, capacity: 60, trend: 'stable', provider: 'native' },
  'lossinsights': { current: 22, capacity: 50, trend: 'up', provider: 'native' },
  'eligibilitychecker': { current: 42, capacity: 90, trend: 'up', provider: 'native' },
  'insurancecontrolsadvisor': { current: 18, capacity: 70, trend: 'stable', provider: 'native' },
  'compliancechecker': { current: 38, capacity: 80, trend: 'up', provider: 'native' },
  'marketintel': { current: 15, capacity: 45, trend: 'down', provider: 'native' },
  'documentextractor': { current: 29, capacity: 85, trend: 'stable', provider: 'native' },
  'regulatory-monitor': { current: 18, capacity: 50, trend: 'up', provider: 'native' },
  
  // Hosted agents
  'fraud-detector': { current: 21, capacity: 40, trend: 'stable', provider: 'hosted' },
  'settlement-analyzer': { current: 16, capacity: 35, trend: 'up', provider: 'hosted' },
  'riskscorer': { current: 28, capacity: 60, trend: 'stable', provider: 'hosted' },
};